import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash.debounce'
import { Document, pdfjs } from 'react-pdf'

import styled from '@emotion/styled'
import SignBox from 'portal-modules/documents/components/SignBox'
import { connect } from 'react-redux'
import { requestSignatureImages } from 'portal-modules/documents/redux/modules/documents'
import PageView from 'portal-modules/documents/components/PdfView/PageView'
import { DOCUMENTS } from 'portal-modules/documents/constants/urls'
import { IS_COOKIE_FUNCTIONALITY_ENABLED } from 'portal-modules/common/constants/keys'
import getProjectName from 'portal-modules/common/utils/get-project-name'

// Removed react-file-viewer (v1.2.1) because it is unmaintaned,
// if the library is upgraded then re-install it with a new version and remove react-office-viewer and @cyntler/react-doc-viewer.
// import FileViewer from 'react-file-viewer'

// A Excel sheet viewer. It will work only for .xlsx, .xls formats.
import { SheetViewer } from 'react-office-viewer'
// New doc viewer to view documents. It will work for non-PDF and non-XLSX docs.
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer'
import '@cyntler/react-doc-viewer/dist/index.css'

// react-pdf new version needed annotation css
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
// Added pdf-js at common\\modules\\common\\workers\\pdf-4.3.136.worker.min.js.
// import * as fakeWrker from 'portal-modules/common/workers/pdf-4.3.136.worker.min.js'
// import * as fakeWrker from 'portal-modules/common/workers/pdf-4.4.168.worker.min.js'

const wrker = new URL(
  // Upgrade pdf-js in case you update react-pdf version.
  'portal-modules/common/workers/pdf-4.4.168.worker.min.js',
  import.meta.url,
).toString()

// Needed fake worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = wrker

const PdfWrapper = styled('div')`
  .pdf-page-wrapper {
    overflow: hidden;
    z-index: 1;
    position: relative;

    /* Ensure proper stacking context for signature boxes on iOS Safari */
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
  }
`

const StyledDiv = styled('div')`
  height: 100%;
  > div {
    overflow-y: auto;
  }
  .pdf-viewer-container .pdf-canvas {
    display: flex;
    justify-content: center;
  }

  // Overriding default styles of SheetViewer
  #wbSheets_wrapper_id {
    // Hiding download button because it's downloading a file in wrong format format
    button[title='Download'] {
      display: none;
    }

    // Making the sheet un-editable
    .handsontable table.htCore {
      pointer-events: none;
    }

    // Styling a first row as bold
    table.htCore tbody tr:first-child {
      font-weight: bold;
    }

    // Hiding Non-required column headers
    .ht_clone_top_inline_start_corner .ht_clone_top_left_corner .handsontable,
    table.htCore thead,
    .ht_clone_top .handsontable,
    .ht_master handsontable {
      display: none;
    }

    // Hiding Non-required row counter
    .handsontable col.rowHeader {
      width: 0 !important;
    }

    .ht_clone_inline_start.ht_clone_left .handsontable col.rowHeader {
      width: 0px !important;
    }

    // Sheet should take 100% width
    .ht_master table.htCore,
    .ht_master .wtSpreader,
    .ht_master .wtHider {
      width: 100% !important;
    }
  }

  // Overriding react-doc-viewer styles
  #react-doc-viewer {
    background: transparent;

    #image-renderer {
      background-image: none;
      background-color: transparent;
    }
  }
`
class PdfView extends Component {
  constructor(props) {
    super(props)

    this.state = {
      numPages: null,
      pageNumber: props.pageNumber,
      heightRatio: 0,
      pageWidth: 0,
      pageHeight: 0,
      // Keep track of individual page ratios/heights to handle documents with varying page sizes
      pageRatios: [],
      pageHeights: [],
      // if file has sign boxes then set file path to empty so that we can triggger our custom file download call
      file: this.props.signingBoxes?.length
        ? ''
        : this.getFileRequest(this.props.file),
      fullSignature: null,
      initials: null,
    }
    this.pdfWrapper = React.createRef()
  }

  /**
   * Determines if cookie-based authentication should be used
   * Only ResidentPortal (rp) should use cookie-based authentication
   * TenantPortal (tp) and ReservationHub (rh) should always use Bearer token
   * @returns {boolean} true if cookie auth should be used, false if Bearer token should be used
   */
  shouldUseCookieAuthentication = () => {
    const projectName = getProjectName()
    return projectName === 'rp' && IS_COOKIE_FUNCTIONALITY_ENABLED
  }

  onDocumentLoad = pdf => {
    const { pageNumber, onLoad } = this.props
    const numPages = pdf.numPages

    /*
     * Calculate height to width ratio for every page so that we can render
     * pages with different sizes (e.g. portrait & landscape pages) without
     * causing overlaps or large gaps.
     */
    const ratioPromises = Array.from({ length: numPages }, (_, i) =>
      pdf.getPage(i + 1).then(page => {
        const viewport = page.getViewport({ scale: 1 })
        return viewport.height / viewport.width
      }),
    )

    Promise.all(ratioPromises).then(ratios => {
      const { pageWidth } = this.state

      this.setState({
        heightRatio: ratios[0] || 0,
        pageRatios: ratios,
        pageHeights: ratios.map(r => pageWidth * r),
      })
    })

    this.setState({
      numPages,
      pageNumber:
        pageNumber > numPages ? numPages : pageNumber > 0 ? pageNumber : 1,
    })

    onLoad &&
      onLoad({
        pageCount: numPages,
        nextPage: this.handleLoadNextPage,
        prevPage: this.handleLoadPreviousPage,
      })
  }

  handleResizeAndPageRender = debounce(() => {
    if (!this.pdfWrapper) {
      return
    }

    const clientWidth = this.pdfWrapper.current?.clientWidth
    const { heightRatio, pageWidth } = this.state
    if (clientWidth == pageWidth) {
      return
    }

    this.setState(prevState => ({
      pageWidth: clientWidth,
      pageHeight: clientWidth * heightRatio,
      pageHeights: prevState.pageRatios.length
        ? prevState.pageRatios.map(r => clientWidth * r)
        : prevState.pageHeights,
    }))
  }, 250)

  handleLoadPreviousPage = () => {
    this.setState(
      ({ pageNumber }) => ({ pageNumber: pageNumber - 1 }),
      () =>
        this.props.onPageChange({
          pageNumber: this.state.pageNumber,
        }),
    )
  }

  handleLoadNextPage = () => {
    this.setState(
      ({ pageNumber }) => ({ pageNumber: pageNumber + 1 }),
      () =>
        this.props.onPageChange({
          pageNumber: this.state.pageNumber,
        }),
    )
  }

  getFileRequest = file => {
    let authHeader = !this.shouldUseCookieAuthentication() ? { Authorization: `Bearer ${this.props.token}`} : ''
    return {
      url: file,
      httpHeaders: {
        'Content-Type': 'application/json',
        ...authHeader
      },
    }
  }

  getAuthToken = () => {
    return !this.shouldUseCookieAuthentication() ? `&auth_token=${this.props.token}` : ''
  }

  renderPages = () => {
    let { numPages } = this.state

    // No pages exists or loaded yet, return
    if (numPages === null) return

    const { pageNumber, pageWidth, pageHeight, pageHeights } = this.state
    const { scale } = this.props
    let pages = []
    let signBoxes = []
    if (!this.props.renderAllPages) numPages = pageNumber
    let numLoops = this.props.displayPageNumbers || numPages
    for (let index = pageNumber; index <= numLoops; index++) {
      // code to load signbox components on preview
      let signBoxesUI = []
      if (this.props.signingBoxes.length) {
        this.props.signingBoxes.forEach(signBox => {
          if (signBox.page_number == index) {
            signBoxesUI.push(
              <SignBox
                key={index + signBox.box_id}
                signBox={signBox}
                pageWidth={pageWidth}
                signatureImage={
                  signBox.is_initials_only
                    ? this.state.initials
                    : this.state.fullSignature
                }
              />,
            )
          }
        })
      }

      signBoxes.push(signBoxesUI)
    }

    if (this.props.applyLazyLoad) {
      return (
        <PageView
          numPages={numPages}
          width={pageWidth}
          pageHeights={pageHeights}
          height={pageHeights[0] || pageHeight || 600}
          scale={scale}
          lazyLoad={this.props.applyLazyLoad}
          handleResizeAndPageRender={this.handleResizeAndPageRender}
          signingBoxes={signBoxes}
        />
      )
    } else {
      for (let index = pageNumber; index <= numLoops; index++) {
        let signBoxesUI = signBoxes[index - 1]
        let currentHeight = pageHeights[index - 1] || pageHeight
        pages.push(
          <PageView
            numPages={numPages}
            width={pageWidth}
            height={currentHeight}
            scale={scale}
            lazyLoad={this.props.applyLazyLoad}
            handleResizeAndPageRender={this.handleResizeAndPageRender}
            pageIndex={index}
            signingBoxes={signBoxesUI}
          />,
        )
      }
      return pages
    }
  }

  loadSignatureImages = ({
    signingBoxes,
    requestSignatureImages,
    file_id,
    file,
  }) => {
    // load resident signature images, if document has signaure merge fields
    if (signingBoxes?.length) {
      this.setState({ file: '' })
      requestSignatureImages({
        payload: {
          id: file_id,
          signature_images: 1,
        },
        callback: result => {
          this.setState({ ...result })
        },
      })
    } else {
      // set new file path in state
      this.setState({ file: this.getFileRequest(file) })
    }
  }

  componentWillMount = () => {
    if (
      !this.props.signDocument &&
      !this.props.marketingName &&
      this.props.history
    ) {
      // After redirecting from login, we are unable to view the document immediately because marketingName is undefined, causing an API error.
      // To handle this, we redirect to the document root and clear localStage if the URL contains view.
      // Eg: Before logging in, accessing http://localhost:3000/test/1234/view should redirect to the document root.

      if (
        localStorage.beforeLoginUrl &&
        localStorage.beforeLoginUrl?.includes('view')
      ) {
        this.props.history.push(DOCUMENTS.ROOT)
      } else {
        this.props.history.goBack()
      }
    } else {
      this.handleResizeAndPageRender()
      window.addEventListener('resize', this.handleResizeAndPageRender)
    }

    this.loadSignatureImages(this.props)
  }

  componentDidUpdate = (prevProps, prevState) => {
    // compare new props/updated props with prev file
    if (
      this.props.file != '' &&
      this.state.file != '' &&
      this.props.file !== prevProps.file
    ) {
      this.loadSignatureImages(this.props)
    }
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.handleResizeAndPageRender)
  }

  render() {
    const { marketingName, signDocument } = this.props

    return (
      <Fragment>
        {signDocument ||
        (marketingName &&
          marketingName
            .substring(marketingName.lastIndexOf('.') + 1)
            .toLowerCase() === 'pdf') ? (
          <PdfWrapper ref={this.pdfWrapper}>
            <Document
              file={this.state.file}
              onLoadSuccess={this.onDocumentLoad}
              error={__('Failed to load document.')}
              loading={__('Loading document...')}
              noData={
                this.state.file == ''
                  ? __('Loading document...')
                  : __('No PDF file specified.')
              }
            >
              {this.renderPages()}
            </Document>
          </PdfWrapper>
        ) : (
          <StyledDiv ref={this.pdfWrapper}>
            {/* Uncomment the FileViewer code only if the library is updated with latest version.*/}
            {/* <FileViewer
                fileType={
                  marketingName &&
                  marketingName
                    .substring(marketingName.lastIndexOf('.') + 1)
                    .toLowerCase()
                }
                filePath={
                  this.state.file.url + '&auth_token=' + this.props.token
                }
              /> */}
            {marketingName &&
            ['xlsx', 'xls', 'csv', 'xcl'].includes(
              marketingName
                ?.substring(marketingName?.lastIndexOf('.') + 1)
                ?.toLowerCase(),
            ) ? (
              <SheetViewer
                file={this.state.file.url + this.getAuthToken()}
                fileName={marketingName
                  .substring(marketingName.lastIndexOf('.') + 1)
                  .toLowerCase()}
                // timeout={10000}
                locale='en'
                height='auto'
              />
            ) : (
              <DocViewer
                pluginRenderers={DocViewerRenderers}
                prefetchMethod='GET'
                documents={[
                  {
                    uri:
                      this.state.file.url + this.getAuthToken(),
                    fileType: marketingName
                      .substring(marketingName.lastIndexOf('.') + 1)
                      .toLowerCase(),
                  },
                ]}
                config={{
                  header: {
                    disableHeader: true,
                    disableFileName: false,
                    retainURLParams: false,
                  },
                }}
              />
            )}
          </StyledDiv>
        )}
      </Fragment>
    )
  }
}

PdfView.propTypes = {
  file: PropTypes.string.isRequired,
  file_id: PropTypes.number,
  onLoad: PropTypes.func,
  onPageChange: PropTypes.func,
  pageNumber: PropTypes.number,
  renderAllPages: PropTypes.bool,
  marketingName: PropTypes.string,
  signDocument: PropTypes.bool,
  history: PropTypes.object,
  displayPageNumbers: PropTypes.number,
  pageWidth: PropTypes.number,
  pageHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  signingBoxes: PropTypes.array,
  initials: PropTypes.string,
  fullSignature: PropTypes.string,
  requestSignatureImages: PropTypes.func.isRequired,
  scale: PropTypes.number,
  applyLazyLoad: PropTypes.bool,
}

PdfView.defaultProps = {
  pageNumber: 1,
  renderAllPages: true,
  signDocument: false,
  signingBoxes: [],
  scale: 1,
}

export default connect(
  ({ user, leases }) => {
    return {
      token: Object.keys(leases)?.length
        ? leases[user?.leaseId]?.token
        : user.token,
    }
  },
  { requestSignatureImages },
)(PdfView)
